import { ILocateUserEntity, ILocateUserReqBody } from '../../entity/interface/iLocateUser';
import { IVerifyEmailGenerateResponse, IVerifyEmailVerifyPinResponse } from '../../entity/interface/iVerifyEmail';
import {
    ILoginSuccessResponse,
    ILoginOtpStep1Response,
    ILoginOtpStep2Response,
    ILoginStep1Response,
} from '../../entity/interface/iLoginEntities';
import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { API_IDS } from '@intigral/constant';
import {
    IAccountActivationPayload,
    IAccountActivationResponse,
    IForgotPasswordGeneratePinEntity,
    IForgotPasswordUpdateEmailReqBody,
    IForgotPasswordVerifyPinEntity,
    IGeneratePinEntity,
    IGeneratePinReqBody,
    IResetPasswordStep1Entity,
    IResetPasswordStep1ReqBody,
    IResetPasswordStep2Entity,
    IResetPasswordStep2ReqBody,
    IVerifyPinEntity,
    IVerifyPinReqBody,
    IVerifyUserResponse,
    IPaymentSwitchGenerateParams,
    IPaymentSwitchVerifyParams,
    CSRFTokenRequest,
    IReSubscribeOtpSendResponse,
    IReSubscribeOtpSendParams,
    IReSubscribeOtpVerifyParams,
    IReSubscribeOtpVerifyResponse,
    IReSubscribeCreditCardParams,
    IReSubscribeCreditCardResponse,
    IPaymentConfigEntity,
    IUnsubscribeReqBody,
    IUnsubscribeEntity,
    ISecurityParams,
} from '../../entity';
import {
    IQrCodeLinkRequest,
    IQrCodePairResponse,
    IQrCodeSuccessResponse,
    IQrGenerateCodeResponse,
} from '../../entity/interface/iQrCodes';
import { generateBoltSignatureHeader } from '@intigral/utils';
import { IApisLoginResponse } from '../../entity/interface/iAuthApis';
import { ILegacyMigrateRequest, ILegacyMigrateResponse } from '../../entity/interface/iUserApis';
import { container } from '../../DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IApiPathManager } from '../config/ApiPathManager';
import { IUserRepository } from '../../core/repositories/interfaces/iUserRepository';

class UserRepository implements IUserRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiV2Http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    private get appKey(): string {
        return process.env['NX_PUBLIC_APP_KEY'] || 'APP_KEY';
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }
    async sendAccountActivation(
        payload: IAccountActivationPayload,
        csrfToken: { [key: string]: string }
    ): Promise<IAccountActivationResponse> {
        const path = this.apiPathManager.getPath(API_IDS.USER_PARTNER_ACTIVATE_LINK);
        const res = await this.http.post<IAccountActivationResponse>(path, {
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                ...payload,
                apiKey: payload.userApiKey,
                partnerName: payload.partnerName.toLowerCase(),
                ...csrfToken,
            },
        });

        return res.data;
    }

    forgotPasswordGeneratePinByEmail(email: string): Promise<IForgotPasswordGeneratePinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_GENERATE_PIN_BY_EMAIL);
        return this.http
            .post<IForgotPasswordGeneratePinEntity>(path, {
                params: {
                    email,
                    appKey: this.appKey,
                },
            })
            .then((res) => res.data);
    }
    forgotPasswordGeneratePinByMsisdn(msisdn: string): Promise<IForgotPasswordGeneratePinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_GENERATE_PIN_BY_MSISDN);
        return this.http
            .post<IForgotPasswordGeneratePinEntity>(path, {
                params: {
                    msisdn,
                    appKey: this.appKey,
                },
            })
            .then((res) => res.data);
    }
    forgotPasswordVerifyPinByEmail(otp: string, verificationGuid: string): Promise<IForgotPasswordVerifyPinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_VERIFIY_BY_EMAIL);
        return this.http
            .put<IForgotPasswordVerifyPinEntity>(path, {
                params: {
                    verificationGuid,
                    appKey: this.appKey,
                },
                body: {
                    otp,
                },
            })
            .then((res) => res.data);
    }
    forgotPasswordVerifyPinByMsisdn(otp: string, verificationGuid: string): Promise<IForgotPasswordVerifyPinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_VERIFIY_BY_MSISDN);
        return this.http
            .put<IForgotPasswordVerifyPinEntity>(path, {
                params: {
                    verificationGuid,
                    appKey: this.appKey,
                },
                body: {
                    otp,
                },
            })
            .then((res) => res.data);
    }
    forgotPasswordUpdatePasswordByEmail(
        reqBody: IForgotPasswordUpdateEmailReqBody
    ): Promise<IForgotPasswordVerifyPinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_UPDATE_BY_EMAIL);
        return this.http
            .put<IForgotPasswordVerifyPinEntity>(path, {
                params: {
                    appKey: this.appKey,
                },
                body: reqBody,
            })
            .then((res) => res.data);
    }
    forgotPasswordUpdatePasswordByMsisdn(
        reqBody: IForgotPasswordUpdateEmailReqBody
    ): Promise<IForgotPasswordVerifyPinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_FORGOT_PASSWORD_UPDATE_BY_MSISDN);
        return this.http
            .put<IForgotPasswordVerifyPinEntity>(path, {
                params: {
                    appKey: this.appKey,
                },
                body: reqBody,
            })
            .then((res) => res.data);
    }
    resetPasswordStep1(lang: string, reqBody: IResetPasswordStep1ReqBody): Promise<IResetPasswordStep1Entity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_RESET_PASSWORD_STEP1);
        return this.http
            .post<IResetPasswordStep1Entity>(path, {
                body: reqBody,
                headers: {
                    'Accept-Language': lang,
                },
                params: {
                    appKey: this.appKey,
                },
            })
            .then((result) => result.data);
    }
    resetPaswordStep2(lang: string, reqBody: IResetPasswordStep2ReqBody): Promise<IResetPasswordStep2Entity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_RESET_PASSWORD_STEP2);
        return this.http
            .post<IResetPasswordStep2Entity>(path, {
                body: reqBody,
                headers: {
                    'Accept-Language': lang,
                },
                params: {
                    appKey: this.appKey,
                },
            })
            .then((result) => result.data);
    }
    generatePin(reqBody: IGeneratePinReqBody): Promise<IGeneratePinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_GENERATE_PIN);
        return this.http.post<IGeneratePinEntity>(path, { body: reqBody }).then((res) => res.data);
    }

    verifyPin(reqBody: IVerifyPinReqBody): Promise<IVerifyPinEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_GENERATE_PIN);
        return this.http.post<IVerifyPinEntity>(path, { body: reqBody }).then((res) => res.data);
    }

    async locateUser(reqBody: ILocateUserReqBody): Promise<ILocateUserEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_LOCATE_USER);
        return this.http
            .post<ILocateUserEntity>(path, {
                withCredentials: true,
                body: reqBody,
                params: {
                    appKey: this.appKey,
                },
            })
            .then((res) => res.data);
    }

    async generateEmailVerifcationPin(email: string, language: string): Promise<boolean> {
        const path = this.apiPathManager.getPath(API_IDS.USER_EMAIL_VERIFICATION_GENERATE_PIN);
        const response = await this.http.post<IVerifyEmailGenerateResponse>(path, {
            withCredentials: true,
            params: {
                appKey: this.appKey,
                email: email,
            },
            headers: {
                'Accept-Language': language,
            },
        });

        if (response.data?.data?.verificationStatus === 'NEW') {
            return true;
        }
        return false;
    }

    async verifyEmailVerifcationPin(guid: string): Promise<boolean> {
        const path = this.apiPathManager.getPath(API_IDS.USER_EMAIL_VERIFICATION_VERIFY_PIN);
        const response = await this.http.put<IVerifyEmailVerifyPinResponse>(path, {
            withCredentials: true,
            params: {
                appKey: this.appKey,
                verificationGuid: guid,
            },
        });
        if (response?.data?.status === 'VERIFIED') {
            return true;
        }
        return false;
    }

    async loginStep1(username: string, agent: string): Promise<ILoginStep1Response> {
        const path = this.apiPathManager.getPath(API_IDS.USER_LOGIN_STEP_1);
        const response = await this.http.post<ILoginStep1Response>(path, {
            withCredentials: true,
            body: {
                username: username,
                userAgent: agent,
            },
            params: {
                appKey: this.appKey,
            },
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    }

    async loginCredentials(
        username: string,
        password: string,
        agent: string,
        tokenNeeded: boolean
    ): Promise<ILoginSuccessResponse> {
        const path = this.apiPathManager.getPath(API_IDS.USER_LOGIN_CREDENTIALS);
        const response = await this.http.post<ILoginSuccessResponse>(path, {
            withCredentials: true,
            body: {
                username: username,
                password: password,
                userAgent: agent,
                tokenNeeded: tokenNeeded,
            },
            params: {
                appKey: this.appKey,
            },
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    }

    async loginWithToken(
        username: string,
        agent: string,
        grantType: string,
        token: string,
        identityToken: string
    ): Promise<IApisLoginResponse> {
        const path = this.apiPathManager.getPath(API_IDS.USER_LOGIN_WITH_TOKEN);
        const response = await this.http.post<IApisLoginResponse>(path, {
            withCredentials: true,
            body: {
                username: username,
                userAgent: agent,
                grantType: grantType,
            },
            params: {
                appKey: this.appKey,
            },
            headers: {
                'Content-Type': 'application/json',
                token: token,
                identityToken: identityToken,
            },
        });
        return response.data;
    }

    async loginOtpStep1(language: string, username: string, agent: string): Promise<ILoginOtpStep1Response> {
        const path = this.apiPathManager.getPath(API_IDS.USER_LOGIN_OTP);
        const response = await this.http.post<ILoginOtpStep1Response>(path, {
            withCredentials: true,
            body: {
                username: username,
                userAgent: agent,
            },
            params: {
                appKey: this.appKey,
            },
            headers: {
                'Accept-Language': language,
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    }

    async loginOtpStep2(
        language: string,
        username: string,
        agent: string,
        otp: string
    ): Promise<ILoginOtpStep2Response> {
        const path = this.apiPathManager.getPath(API_IDS.USER_LOGIN_OTP);
        const response = await this.http.put<ILoginOtpStep2Response>(path, {
            withCredentials: true,
            body: {
                username: username,
                userAgent: agent,
                otp: otp,
            },
            params: {
                appKey: this.appKey,
            },
            headers: {
                'Accept-Language': language,
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    }

    async qrGenerateCode(
        type: 'SIGN_IN' | 'SIGN_UP',
        username: string | undefined,
        timestamp: number,
        payload: string | { [key: string]: any },
        language: 'ar' | 'en'
    ): Promise<IQrGenerateCodeResponse> {
        const path = this.apiPathManager.getPath(API_IDS.QR_GENERATE_CODE);
        const parsedPayload = typeof payload === 'string' ? JSON.parse(payload) : payload;
        const response = await this.http.post<IQrGenerateCodeResponse>(path, {
            withCredentials: true,
            params: {
                appKey: this.appKey,
                username: username,
                type: type,
            },
            headers: {
                signature: generateBoltSignatureHeader(parsedPayload, timestamp, 'POST'),
                'Accept-Language': language,
                epochMs: timestamp.toString(),
                'Content-Type': 'application/json',
            },
            body: parsedPayload,
        });
        return response.data;
    }

    async qrAuthenticateCode(
        apiKey: string,
        qrCode: string,
        token: string,
        identityToken: string
    ): Promise<IQrCodeSuccessResponse> {
        const path = this.apiPathManager.getPath(API_IDS.QR_AUTHENTICATE_CODE);
        const response = await this.http.put<IQrCodeSuccessResponse>(path, {
            withCredentials: true,
            params: {
                apiKey: apiKey,
                code: qrCode,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });
        return response.data;
    }

    async qrCodeLogin(
        qrCode: string,
        timestamp: number,
        language: string,
        payload: string | { [key: string]: any }
    ): Promise<ILoginSuccessResponse> {
        const path = this.apiPathManager.getPath(API_IDS.QR_CODE_LOGIN);
        const parsedPayload = typeof payload === 'string' ? JSON.parse(payload) : payload;
        const response = await this.http.post<ILoginSuccessResponse>(path, {
            withCredentials: true,
            params: {
                appKey: this.appKey,
                qrcode: qrCode,
            },
            headers: {
                epochMs: timestamp.toString(),
                signature: generateBoltSignatureHeader(parsedPayload, timestamp, 'POST'),
                'Accept-Language': language,
            },
            body: parsedPayload,
        });
        return response.data;
    }

    async qrCodePairing(bucketId: string): Promise<IQrCodePairResponse> {
        const path = this.apiPathManager.getPath(API_IDS.QR_CODE_PAIRING);
        const response = await this.http.get<IQrCodePairResponse>(path, {
            withCredentials: true,
            params: {
                bucketId: bucketId,
            },
        });
        return response.data;
    }

    async qrCodeLink(payload: IQrCodeLinkRequest): Promise<IQrCodeSuccessResponse> {
        const path = this.apiPathManager.getPath(API_IDS.QR_CODE_LINK);
        const response = await this.http.put<IQrCodeSuccessResponse>(path, {
            withCredentials: true,
            params: {
                appKey: this.appKey,
            },
            body: payload,
        });
        return response.data;
    }

    async internalVerifyUser(
        username: string,
        recaptchaToken: string,
        csrfToken: CSRFTokenRequest
    ): Promise<IVerifyUserResponse> {
        const path = this.apiPathManager.getPath(API_IDS.INTERNAL_VERIFY_USER);
        const response = await this.http.post<IVerifyUserResponse>(path, {
            withCredentials: true,
            body: {
                username: username,
                ...csrfToken,
            },
        });
        return response.data;
    }

    async paymentSwitchGenerate(payload: IPaymentSwitchGenerateParams, csrfToken: CSRFTokenRequest): Promise<any> {
        const path = this.apiPathManager.getPath(API_IDS.PAYMENT_SWITCH_GENERATE);
        const response = await this.http.post<any>(path, {
            withCredentials: true,
            body: {
                ...payload,
                ...csrfToken,
            },
        });
        return response.data;
    }

    async paymentSwitchVerify(payload: IPaymentSwitchVerifyParams, csrfToken: CSRFTokenRequest): Promise<any> {
        const path = this.apiPathManager.getPath(API_IDS.PAYMENT_SWITCH_VERIFY);
        const response = await this.http.post<any>(path, {
            withCredentials: true,
            body: {
                ...payload,
                ...csrfToken,
            },
        });
        return response.data;
    }

    async resubscribeOtpSend(
        payload: IReSubscribeOtpSendParams,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: { [key: string]: string }
    ): Promise<IReSubscribeOtpSendResponse> {
        const path = this.apiPathManager.getPath(API_IDS.RESUBSCRIBE_OTP_SEND);
        const response = await this.apiV2Http.post<IReSubscribeOtpSendResponse>(path, {
            withCredentials: true,
            body: {
                ...payload,
                authToken,
                authSign,
                identityToken,
                ...csrfToken,
            },
        });
        return response.data;
    }

    async resubscribeOtpVerify(
        payload: IReSubscribeOtpVerifyParams,
        authToken: string,
        authSign: string,
        identityToken: string,
        csrfToken: { [key: string]: string }
    ): Promise<IReSubscribeOtpVerifyResponse> {
        const path = this.apiPathManager.getPath(API_IDS.RESUBSCRIBE_OTP_VERIFY);
        const response = await this.apiV2Http.post<IReSubscribeOtpVerifyResponse>(path, {
            withCredentials: true,
            body: {
                ...payload,
                authToken,
                authSign,
                identityToken,
                ...csrfToken,
            },
        });
        return response.data;
    }

    async resubscribeCreditCard(
        payload: IReSubscribeCreditCardParams,
        authToken: string,
        authSign: string,
        csrfToken: { [key: string]: string }
    ): Promise<IReSubscribeCreditCardResponse> {
        const path = this.apiPathManager.getPath(API_IDS.RESUBSCRIBE_CREDIT_CARD);
        const response = await this.apiV2Http.post<IReSubscribeCreditCardResponse>(path, {
            withCredentials: true,
            body: {
                ...payload,
                authToken,
                authSign,
                ...csrfToken,
            },
        });
        return response.data;
    }

    async getCountryConfigs(
        username: string,
        identityToken: string,
        apiKey: string,
        authToken: string,
        authSign: string,
        csrfToken: { [key: string]: string }
    ): Promise<IPaymentConfigEntity> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_COUNTRY_CONFIGS);
        const response = await this.apiV2Http.post<IReSubscribeCreditCardResponse>(path, {
            withCredentials: true,
            body: {
                username,
                identityToken,
                apiKey,
                authToken,
                authSign,
                ...csrfToken,
            },
        });
        return response.data;
    }

    unsubscribe(
        username: string,
        authToken: string,
        authSign: string,
        csrfToken: { [key: string]: string },
        reqBody: IUnsubscribeReqBody
    ): Promise<IUnsubscribeEntity> {
        const path = this.apiPathManager.getPath(API_IDS.USER_UNSUBSCRIBE);
        return this.apiV2Http
            .post<IUnsubscribeEntity>(path, {
                withCredentials: true,
                body: {
                    username,
                    authToken,
                    authSign,
                    ...csrfToken,
                    ...reqBody,
                },
            })
            .then((result) => result.data);
    }

    legacyMigrate(
        payload: ILegacyMigrateRequest,
        authToken: string,
        authSign: string,
        csrfToken: { [key: string]: string }
    ): Promise<ILegacyMigrateResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_LEGACY_CC_MIGRATE);
        return this.apiV2Http
            .post<ILegacyMigrateResponse>(path, {
                withCredentials: true,
                body: {
                    authToken,
                    authSign,
                    ...csrfToken,
                    ...payload,
                },
            })
            .then((result) => result.data);
    }

    legacyMigrateGenerate(
        payload: ILegacyMigrateRequest,
        authToken: string,
        authSign: string,
        csrfToken: { [key: string]: string }
    ): Promise<ILegacyMigrateResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_LEGACY_DCB_MIGRATE_GENERATE);
        return this.apiV2Http
            .post<ILegacyMigrateResponse>(path, {
                withCredentials: true,
                body: {
                    authToken,
                    authSign,
                    ...csrfToken,
                    ...payload,
                },
            })
            .then((result) => result.data);
    }

    legacyMigrateVerify(
        payload: ILegacyMigrateRequest,
        authToken: string,
        authSign: string,
        csrfToken: { [key: string]: string }
    ): Promise<ILegacyMigrateResponse> {
        const path = this.apiPathManager.getPath(API_IDS.SLP_LEGACY_DCB_MIGRATE_VERIFY);
        return this.apiV2Http
            .post<ILegacyMigrateResponse>(path, {
                withCredentials: true,
                body: {
                    authToken,
                    authSign,
                    ...csrfToken,
                    ...payload,
                },
            })
            .then((result) => result.data);
    }
}

export default UserRepository;
